import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { UserPlus, Trash2, PowerOff, Power, Mic2 } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";

interface ManagedHost {
  id: string;
  username: string;
  display_name: string;
  is_active: boolean;
  venue_id: string | null;
  venue_name: string | null;
  created_at: string;
}

interface Props {
  venueId: string;
  venueName: string;
}

const ManageHostsSection = ({ venueId, venueName }: Props) => {
  const { toast } = useToast();
  const sessionToken = localStorage.getItem("hostSessionToken") ?? "";

  const [hosts, setHosts] = useState<ManagedHost[]>([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);

  // Create form state
  const [newUsername, setNewUsername] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [newDisplayName, setNewDisplayName] = useState("");
  const [creating, setCreating] = useState(false);

  // ── Load managed hosts ──────────────────────────────────────────────────────
  const loadHosts = async () => {
    setLoading(true);
    try {
      const { data, error } = await (supabase.rpc as any)("list_managed_hosts", {
        p_session_token: sessionToken,
      });
      if (error) throw error;
      setHosts(data ?? []);
    } catch (e: any) {
      console.error("Failed to load managed hosts:", e);
      toast({ title: "Error loading hosts", description: e?.message ?? String(e), variant: "destructive" });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadHosts(); }, []);

  // ── Create host ─────────────────────────────────────────────────────────────
  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newUsername.trim() || !newPassword || !newDisplayName.trim()) return;
    setCreating(true);
    try {
      const { error } = await (supabase.rpc as any)("create_managed_host", {
        p_session_token: sessionToken,
        p_venue_id: venueId,
        p_username: newUsername.trim().toLowerCase(),
        p_password: newPassword,
        p_display_name: newDisplayName.trim(),
      });
      if (error) {
        const msg = String(error.message);
        if (msg.includes("username_taken")) {
          toast({ title: "Username taken", description: "Choose a different username.", variant: "destructive" });
        } else if (msg.includes("host_limit_reached")) {
          toast({ title: "Limit reached", description: "Enterprise accounts can only have one show host.", variant: "destructive" });
        } else {
          throw error;
        }
        return;
      }
      toast({ title: "Host created", description: `${newDisplayName} can now sign in at /show-host` });
      setNewUsername(""); setNewPassword(""); setNewDisplayName("");
      setDialogOpen(false);
      loadHosts();
    } catch (e) {
      console.error(e);
      toast({ title: "Error", description: "Failed to create host account", variant: "destructive" });
    } finally {
      setCreating(false);
    }
  };

  // ── Toggle active ───────────────────────────────────────────────────────────
  const toggleActive = async (host: ManagedHost) => {
    try {
      const { error } = await (supabase.rpc as any)("set_managed_host_active", {
        p_session_token: sessionToken,
        p_target_host_id: host.id,
        p_is_active: !host.is_active,
      });
      if (error) throw error;
      setHosts((prev) => prev.map((h) => h.id === host.id ? { ...h, is_active: !h.is_active } : h));
      toast({ title: host.is_active ? "Host deactivated" : "Host activated" });
    } catch (e) {
      toast({ title: "Error", description: "Failed to update host", variant: "destructive" });
    }
  };

  // ── Delete host ─────────────────────────────────────────────────────────────
  const deleteHost = async (host: ManagedHost) => {
    if (!confirm(`Delete host account "${host.display_name}"? This cannot be undone.`)) return;
    try {
      const { error } = await (supabase.rpc as any)("delete_managed_host", {
        p_session_token: sessionToken,
        p_target_host_id: host.id,
      });
      if (error) throw error;
      setHosts((prev) => prev.filter((h) => h.id !== host.id));
      toast({ title: "Host deleted" });
    } catch (e) {
      toast({ title: "Error", description: "Failed to delete host", variant: "destructive" });
    }
  };

  return (
    <Card className="bg-white/5 border-white/10">
      <CardHeader>
        <div className="flex items-center justify-between gap-3">
          <div className="flex items-center gap-2">
            <Mic2 className="h-4 w-4 text-purple-400" />
            <CardTitle className="text-white text-base">Show Hosts</CardTitle>
          </div>
          {!loading && hosts.length === 0 && (
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button size="sm" className="bg-purple-600 hover:bg-purple-700 text-white gap-1.5">
                <UserPlus className="h-3.5 w-3.5" />
                Add Host
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-slate-900 border-white/10 text-white">
              <DialogHeader>
                <DialogTitle>Create Show Host</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleCreate} className="space-y-4 mt-2">
                <p className="text-sm text-gray-400">
                  This host will be scoped to <span className="text-white font-medium">{venueName}</span> and can only access Show Mode.
                </p>
                <div className="space-y-1.5">
                  <Label>Display Name</Label>
                  <Input
                    value={newDisplayName}
                    onChange={(e) => setNewDisplayName(e.target.value)}
                    placeholder="e.g. Mike the Host"
                    className="bg-white/10 border-white/20 text-white placeholder:text-gray-500"
                    required
                  />
                </div>
                <div className="space-y-1.5">
                  <Label>Username</Label>
                  <Input
                    value={newUsername}
                    onChange={(e) => setNewUsername(e.target.value)}
                    placeholder="e.g. mike_host"
                    className="bg-white/10 border-white/20 text-white placeholder:text-gray-500"
                    required
                  />
                </div>
                <div className="space-y-1.5">
                  <Label>Password</Label>
                  <Input
                    type="password"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    placeholder="••••••••"
                    className="bg-white/10 border-white/20 text-white placeholder:text-gray-500"
                    required
                    minLength={6}
                  />
                </div>
                <Button
                  type="submit"
                  disabled={creating}
                  className="w-full bg-purple-600 hover:bg-purple-700 text-white"
                >
                  {creating ? "Creating…" : "Create Host Account"}
                </Button>
              </form>
            </DialogContent>
          </Dialog>
          )}
        </div>
        <CardDescription>
          Show hosts can run tonight's queue and manage announcements. They cannot access venue settings or the song library.
        </CardDescription>
      </CardHeader>

      <CardContent>
        {loading ? (
          <p className="text-sm text-gray-400 text-center py-4">Loading…</p>
        ) : hosts.length === 0 ? (
          <div className="text-center py-6 space-y-1">
            <Mic2 className="h-8 w-8 text-gray-600 mx-auto" />
            <p className="text-sm text-gray-400">No show hosts yet</p>
            <p className="text-xs text-gray-500">Add a host so someone else can run the show</p>
          </div>
        ) : (
          <div className="space-y-2">
            {hosts.map((host) => (
              <div key={host.id} className="flex items-center gap-3 p-3 rounded-lg bg-white/5 border border-white/10">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <p className="text-sm font-medium text-white truncate">{host.display_name}</p>
                    <Badge variant="outline" className={`text-xs shrink-0 ${host.is_active ? "border-green-500/50 text-green-400" : "border-gray-500/50 text-gray-500"}`}>
                      {host.is_active ? "Active" : "Inactive"}
                    </Badge>
                  </div>
                  <p className="text-xs text-gray-400">@{host.username}</p>
                </div>
                <div className="flex items-center gap-1 shrink-0">
                  <Button
                    size="icon"
                    variant="ghost"
                    className={`h-8 w-8 ${host.is_active ? "text-yellow-400 hover:text-yellow-300" : "text-green-400 hover:text-green-300"}`}
                    title={host.is_active ? "Deactivate" : "Activate"}
                    onClick={() => toggleActive(host)}
                  >
                    {host.is_active ? <PowerOff className="h-3.5 w-3.5" /> : <Power className="h-3.5 w-3.5" />}
                  </Button>
                  <Button
                    size="icon"
                    variant="ghost"
                    className="h-8 w-8 text-red-400 hover:text-red-300"
                    title="Delete host"
                    onClick={() => deleteHost(host)}
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
        <p className="text-xs text-gray-500 mt-3">
          Hosts sign in at <span className="text-gray-400 font-mono">/show-host</span>
        </p>
      </CardContent>
    </Card>
  );
};

export default ManageHostsSection;
